import { assertNever } from "@impero-com/common/utils/obj";
import {
    FilterValueWithMode,
    NO_SAVE_PARAM_NAME,
    PresetSearchParamsCustomDefaultValues,
    formatTableStateSearchUrl,
    CONTROL_ACTIVITIES_DATA_PARAM,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS,
    PersistedControlActivitiesDateFilter,
    TimeToDueDateFilter,
    controlActivitiesListDataParamsCodecMap,
} from "@impero-com/ui/domain";
import { ColumnFiltersState } from "@tanstack/react-table";
import { z } from "zod";

import { CategoryAndTag } from "api/generated/model";
import { DateRange } from "components/filters/dateRange";
import { CONTROL_PROGRAM_STATES } from "org/controlActivities/list/controlProgramStatusFilter";
import { FRICTION_TYPE_FILTER_KEYS } from "org/controlActivities/list/frictionTypeFilter";
import { HealthFilterKey } from "org/controlActivities/list/healthFilter";
import { WebRoute } from "routes";
import {
    EventType,
    ExistingControlActivityStatus,
} from "utils/controlResults/activityStatusUtils";

type ActivityStatusFilter = ExistingControlActivityStatus | EventType;

/**
 * `null` when the dashboard is in `Custom` time-period without bounds picked.
 * In that case the URL is built without a date filter and falls back to the
 * activities page's default window — the dashboard shows no data either, so
 * this URL is effectively unreachable through user interaction.
 */
export interface ActivitiesUrlParams {
    dateRange: DateRange | null;
    statusKey: ActivityStatusFilter | ActivityStatusFilter[] | null;
    filteredControlProgramIds: Array<number>;
    filteredTags: Array<CategoryAndTag>;
    filteredEntityIds: Array<number>;
    showArchivedActivities: boolean;
    /** When false, the URL narrows to activities whose control program is active. */
    showInactivePrograms: boolean;
    healthFilter?: HealthFilterKey;
    frictionTypeFilter?: (typeof FRICTION_TYPE_FILTER_KEYS)[keyof typeof FRICTION_TYPE_FILTER_KEYS][];
    timeToDueDateFilter?: TimeToDueDateFilter;
}

export interface ActivitiesUrlDependencies {
    defaultValues: PresetSearchParamsCustomDefaultValues;
    columnFiltersCodecMap: Record<string, z.ZodCodec>;
    onError?: (error: unknown) => void;
}

/**
 * Builds a canonical URL to the activities page with the given dashboard
 * filters applied as a *temporary* filter: the URL carries the valueless
 * `noSave` param so the activities page renders these filters without persisting
 * them over the user's saved view.
 *
 * This is a pure function; all dependencies must be passed in. To build URLs
 * within a React component, use the `useBuildActivitiesUrl` hook instead.
 */
export function buildCanonicalActivitiesUrl(
    params: ActivitiesUrlParams,
    dependencies: ActivitiesUrlDependencies
): string {
    const {
        dateRange,
        statusKey,
        filteredControlProgramIds,
        filteredTags,
        filteredEntityIds,
        showArchivedActivities,
        showInactivePrograms,
        healthFilter,
        timeToDueDateFilter,
        frictionTypeFilter,
    } = params;
    const { defaultValues, columnFiltersCodecMap, onError } = dependencies;

    const columnFilters = buildColumnFilters(
        statusKey,
        filteredControlProgramIds,
        filteredTags,
        filteredEntityIds,
        healthFilter,
        showInactivePrograms,
        timeToDueDateFilter,
        frictionTypeFilter
    );

    const storedDataParams: Record<string, unknown> = {
        [CONTROL_ACTIVITIES_DATA_PARAM.ARCHIVED]: showArchivedActivities,
        ...(dateRange
            ? {
                  [CONTROL_ACTIVITIES_DATA_PARAM.DATE_FILTER]:
                      buildDueDateFilter(dateRange),
              }
            : {}),
    };

    const url = formatTableStateSearchUrl(
        WebRoute.CONTROL_ACTIVITIES,
        { columnFilters },
        defaultValues,
        columnFiltersCodecMap,
        onError,
        controlActivitiesListDataParamsCodecMap,
        storedDataParams
    );

    // Append the valueless `noSave` marker (formatTableStateSearchUrl would
    // otherwise serialize it as `noSave=`).
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${NO_SAVE_PARAM_NAME}`;
}

// A single-value "is" filter matching the activities list's column filter shape.
function isFilter<T>(value: T[]): FilterValueWithMode<T[]> {
    return { value, mode: "is" };
}

function buildColumnFilters(
    statusKey: ActivitiesUrlParams["statusKey"],
    filteredControlProgramIds: Array<number>,
    filteredTags: Array<CategoryAndTag>,
    filteredEntityIds: Array<number>,
    healthFilter: HealthFilterKey | undefined,
    showInactivePrograms: boolean,
    timeToDueDateFilter: TimeToDueDateFilter | undefined,
    frictionTypeFilter: ActivitiesUrlParams["frictionTypeFilter"]
): ColumnFiltersState {
    const filters: ColumnFiltersState = [];

    if (frictionTypeFilter !== undefined) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.FRICTION_TYPE,
            value: isFilter(frictionTypeFilter),
        });
    }

    if (timeToDueDateFilter !== undefined) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TIME_TO_DUE_DATE,
            value: { mode: "is", value: timeToDueDateFilter },
        });
    }

    if (healthFilter !== undefined) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.HEALTH,
            value: isFilter([healthFilter]),
        });
    }

    // Activities whose program was deleted count as inactive, so they drop out and match BE
    if (!showInactivePrograms) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_STATUS,
            value: isFilter([CONTROL_PROGRAM_STATES.ACTIVE]),
        });
    }

    if (filteredControlProgramIds.length > 0) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.CONTROL_PROGRAM_ID,
            // The column filters by control-program id (rendered as a string).
            value: isFilter(filteredControlProgramIds.map(String)),
        });
    }

    if (filteredEntityIds.length > 0) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.ENTITY,
            // The column filters by entity id (rendered as a string).
            value: isFilter(filteredEntityIds.map(String)),
        });
    }

    if (statusKey !== null) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.WORKFLOW_STATUS,
            value: isFilter(
                (Array.isArray(statusKey) ? statusKey : [statusKey]).flatMap(
                    statusKeyToEventTypes
                )
            ),
        });
    }

    // The dashboard's tag selection maps straight onto the activities tags
    // filter: both AND across categories and OR within one.
    if (filteredTags.length > 0) {
        filters.push({
            id: CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TAGS,
            value: isFilter(filteredTags),
        });
    }

    return filters;
}

function buildDueDateFilter(
    dateRange: DateRange
): PersistedControlActivitiesDateFilter {
    return {
        mode: "dueDate",
        range: [
            dateRange.start.format("YYYY-MM-DD"),
            dateRange.end.format("YYYY-MM-DD"),
        ],
    };
}
function statusKeyToEventTypes(statusKey: ActivityStatusFilter): EventType[] {
    switch (statusKey) {
        case "Completed":
        case "CompletedAfterDueDate":
        case "PendingOnResponsible":
        case "PendingOnFirstReviewer":
        case "PendingOnSecondReviewer":
        case "OverdueOnResponsible":
        case "OverdueOnFirstReviewer":
        case "OverdueOnSecondReviewer":
            return [statusKey];
        case "Pending":
            return [
                "PendingOnResponsible",
                "PendingOnFirstReviewer",
                "PendingOnSecondReviewer",
            ];
        case "Overdue":
            return [
                "OverdueOnResponsible",
                "OverdueOnFirstReviewer",
                "OverdueOnSecondReviewer",
            ];
        default:
            return assertNever(statusKey);
    }
}
